/**
 * Training API
 *
 * RTK Query endpoints for team training functionality:
 * - Organizations and team membership
 * - Training modules
 * - Training assignments
 * - Content sets
 */

import { api } from '../api';

// Types
export type OrgMemberRole = 'owner' | 'admin' | 'manager' | 'member';
export type TrainingAssignmentStatus = 'assigned' | 'in_progress' | 'completed' | 'failed';
export type CoachType = 'system' | 'human' | 'either';
export type EmphasisProfile = 'skill' | 'sale' | 'knowledge' | 'custom';
export type ContentSetOwnerType = 'org' | 'user';
export type ContentItemType = 'file' | 'link' | 'note';
export type EmbeddingStatus = 'pending' | 'indexed' | 'failed';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  ownerUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationMember {
  id: string;
  orgId: string;
  userId: string;
  role: OrgMemberRole;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface OrganizationWithMembers extends Organization {
  members: OrganizationMember[];
}

export interface OrganizationWithRole extends Organization {
  role: OrgMemberRole;
}

/** One immutable published version of a module. */
export interface TrainingModuleVersionSummary {
  id: string;
  version: number;
  contentHash: string;
  publishedAt: string;
  origin: 'authored' | 'legacy_baseline';
}

/** The published version an assignment is bound to (null: unversioned). */
export interface BoundModuleVersion {
  id: string;
  version: number;
  contentHash: string;
  publishedAt: string;
}

export interface TrainingModuleDefinition {
  schemaVersion: number;
  name: string;
  description: string | null;
  domainKey: string;
  scenarioKey: string;
  emphasisProfile: EmphasisProfile;
  coachType: CoachType;
  coachUserId: string | null;
  npcPersonaId: string | null;
  contentSetId: string | null;
  criteria: {
    minOverallScore: number;
    minClarityScore: number | null;
    minStructureScore: number | null;
    minAccuracyScore: number | null;
    minComplexityScore: number | null;
  };
  content: {
    scenarioIds: string[];
    scenarioTags: string[];
    rubric: { id: string; version: string } | null;
    lessons: Array<{ moduleSlug: string; lessonId: string }>;
  };
}

export interface ResolvedModuleContent {
  scenarios: Array<{ id: string; title: string | null }>;
  scenarioTags: string[];
  rubric: { id: string; version: string; title: string | null } | null;
  lessons: Array<{ moduleSlug: string; lessonId: string; moduleTitle: string | null; title: string | null }>;
}

export interface TrainingAssignmentStats {
  total: number;
  assigned: number;
  inProgress: number;
  completed: number;
  failed: number;
}

/** GET /training/modules — one module of the token workspace. */
export interface TrainingModule {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  domainKey: string;
  scenarioKey: string;
  emphasisProfile: EmphasisProfile;
  minOverallScore: number;
  minClarityScore: number | null;
  isActive: boolean;
  /** draft: never published; published: has a version; unversioned: authoring is switched off. */
  status: 'draft' | 'published' | 'unversioned';
  latestVersion: TrainingModuleVersionSummary | null;
  hasDraft: boolean;
  /** Managers only (null for learners). */
  assignmentStats: TrainingAssignmentStats | null;
  assignmentCount: number;
  completedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingModuleDetail extends TrainingModule {
  canManage: boolean;
  definition: TrainingModuleDefinition | null;
  content: ResolvedModuleContent | null;
  draft: { id: string; updatedAt: string; definition: TrainingModuleDefinition; content: ResolvedModuleContent } | null;
  versions: TrainingModuleVersionSummary[];
}

/** What an author sends to create a module or save its draft. */
export interface TrainingModuleInput {
  name: string;
  description?: string | null;
  domainKey: string;
  scenarioKey: string;
  emphasisProfile?: EmphasisProfile;
  minOverallScore: number;
  minClarityScore?: number | null;
  content: {
    scenarioIds: string[];
    scenarioTags: string[];
    rubric: { id: string; version: string } | null;
    lessons: Array<{ moduleSlug: string; lessonId: string }>;
  };
}

/** The canonical content a module may reference (training:write). */
export interface TrainingAuthoringOptions {
  authoringActive: boolean;
  domains: Array<{ key: string; label: string; scenarioKeys: string[] }>;
  rubrics: Array<{ id: string; version: string; title: string }>;
  lessons: Array<{ moduleSlug: string; moduleTitle: string; lessons: Array<{ id: string; title: string }> }>;
  scenarios: Array<{ id: string; title: string; tags: string[] }>;
}

export interface TrainingAssignment {
  id: string;
  moduleId: string;
  orgId: string;
  assigneeUserId: string;
  assignedByUserId: string;
  dueDate?: string;
  status: TrainingAssignmentStatus;
  lastScoreOverall?: number;
  lastClarityScore?: number;
  lastStructureScore?: number;
  lastAccuracyScore?: number;
  lastComplexityScore?: number;
  completedAt?: string;
  createdAt: string;
  /** The module as THIS assignment means it (its bound version's definition). */
  module?: {
    id: string;
    name: string;
    description?: string;
    domainKey: string;
    scenarioKey: string;
    minOverallScore: number;
  };
  /** The published version this assignment is bound to; null when unversioned. */
  moduleVersion?: BoundModuleVersion | null;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export type CreatedAssignment = TrainingAssignment & { outcome: 'created' | 'already_assigned' };

export interface ContentSet {
  id: string;
  ownerType: ContentSetOwnerType;
  ownerId: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContentItem {
  id: string;
  contentSetId: string;
  title: string;
  type: ContentItemType;
  storagePathOrUrl: string;
  metadata?: Record<string, unknown>;
  embeddingStatus: EmbeddingStatus;
  lastIndexedAt?: string;
  indexError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContentSetIndexStatus {
  total: number;
  indexed: number;
  pending: number;
  failed: number;
  ready: boolean;
  /** Whether semantic retrieval exists on this server at all (RC10). */
  retrievalAvailable?: boolean;
  unavailableReason?: string;
}

export interface ContentSetWithItems extends ContentSet {
  items: ContentItem[];
}

export interface SessionConfig {
  type: 'training';
  orgId: string;
  trainingModuleId: string;
  trainingAssignmentId: string;
  domainKey: string;
  scenarioKey: string;
  npcPersonaId?: string;
  coachType: 'system' | 'human';
  coachUserId?: string;
  moduleVersion?: BoundModuleVersion | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const trainingApi = api.enhanceEndpoints({ addTagTypes: ['TrainingAuthoringOptions'] }).injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================
    // Organizations (read-only: the backend retired local org create and
    // member add/update/remove with 410 — membership is Central's)
    // =====================================================

    listOrganizations: builder.query<OrganizationWithRole[], void>({
      query: () => '/orgs',
      transformResponse: (response: ApiResponse<OrganizationWithRole[]>) => response.data,
      providesTags: ['Organizations'],
    }),

    getOrganizationBySlug: builder.query<Organization, string>({
      query: (slug) => `/orgs/by-slug/${slug}`,
      transformResponse: (response: ApiResponse<Organization>) => response.data,
      providesTags: (_result, _error, slug) => [{ type: 'Organizations', id: `slug-${slug}` }],
    }),

    // =====================================================
    // Training Modules
    // =====================================================

    // The workspace is always the token's: no orgId is sent.

    listTrainingModules: builder.query<TrainingModule[], { includeArchived?: boolean } | void>({
      query: (arg) => ({
        url: '/training/modules',
        params: arg && arg.includeArchived ? { includeArchived: 'true' } : undefined,
      }),
      transformResponse: (response: ApiResponse<TrainingModule[]>) => response.data,
      providesTags: (result) => [
        'TrainingModules',
        ...(result ?? []).map((m) => ({ type: 'TrainingModules' as const, id: m.id })),
      ],
    }),

    getTrainingModule: builder.query<TrainingModuleDetail, string>({
      query: (moduleId) => `/training/modules/${moduleId}`,
      transformResponse: (response: ApiResponse<TrainingModuleDetail>) => response.data,
      providesTags: (_result, _error, moduleId) => [{ type: 'TrainingModules', id: moduleId }],
    }),

    getTrainingAuthoringOptions: builder.query<TrainingAuthoringOptions, void>({
      query: () => '/training/modules/authoring-options',
      transformResponse: (response: ApiResponse<TrainingAuthoringOptions>) => response.data,
      providesTags: ['TrainingAuthoringOptions'],
    }),

    /** A new module with its first draft (nothing is assignable until published). */
    createTrainingModule: builder.mutation<TrainingModuleDetail, TrainingModuleInput>({
      query: (body) => ({ url: '/training/modules', method: 'POST', body }),
      transformResponse: (response: ApiResponse<TrainingModuleDetail>) => response.data,
      invalidatesTags: ['TrainingModules'],
    }),

    /** Save the draft. Published versions are never edited. */
    saveTrainingModuleDraft: builder.mutation<TrainingModuleDetail, { moduleId: string; input: TrainingModuleInput }>({
      query: ({ moduleId, input }) => ({ url: `/training/modules/${moduleId}`, method: 'PUT', body: input }),
      transformResponse: (response: ApiResponse<TrainingModuleDetail>) => response.data,
      invalidatesTags: (_result, _error, { moduleId }) => [{ type: 'TrainingModules', id: moduleId }],
    }),

    /** Freeze the draft into the next immutable version. */
    publishTrainingModule: builder.mutation<TrainingModuleDetail, string>({
      query: (moduleId) => ({ url: `/training/modules/${moduleId}/publish`, method: 'POST' }),
      transformResponse: (response: ApiResponse<TrainingModuleDetail>) => response.data,
      invalidatesTags: (_result, _error, moduleId) => ['TrainingModules', { type: 'TrainingModules', id: moduleId }],
    }),

    /** Archive (hide from learners and new assignment) or restore. History is kept. */
    setTrainingModuleArchived: builder.mutation<TrainingModuleDetail, { moduleId: string; archived: boolean }>({
      query: ({ moduleId, archived }) => ({
        url: `/training/modules/${moduleId}/${archived ? 'archive' : 'restore'}`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<TrainingModuleDetail>) => response.data,
      invalidatesTags: (_result, _error, { moduleId }) => ['TrainingModules', { type: 'TrainingModules', id: moduleId }],
    }),

    // =====================================================
    // Training Assignments
    // =====================================================

    createAssignments: builder.mutation<CreatedAssignment[], { moduleId: string; assigneeIds: string[]; dueDate?: string }>({
      query: (body) => ({
        url: '/training/assignments',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CreatedAssignment[]>) => response.data,
      invalidatesTags: (_result, _error, { moduleId }) => ['TrainingAssignments', 'TrainingModules', { type: 'TrainingModules', id: moduleId }],
    }),

    listMyAssignments: builder.query<TrainingAssignment[], void>({
      query: () => '/training/assignments/me',
      transformResponse: (response: ApiResponse<TrainingAssignment[]>) => response.data,
      providesTags: ['TrainingAssignments'],
    }),

    listOrgAssignments: builder.query<TrainingAssignment[], { moduleId?: string } | void>({
      query: (arg) => ({
        url: '/training/assignments',
        params: arg && arg.moduleId ? { moduleId: arg.moduleId } : undefined,
      }),
      transformResponse: (response: ApiResponse<TrainingAssignment[]>) => response.data,
      providesTags: ['TrainingAssignments'],
    }),

    startAssignmentSession: builder.mutation<SessionConfig, string>({
      query: (assignmentId) => ({
        url: `/training/assignments/${assignmentId}/start`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<SessionConfig>) => response.data,
      invalidatesTags: (_result, _error, assignmentId) => [{ type: 'TrainingAssignments', id: assignmentId }],
    }),

    deleteAssignment: builder.mutation<void, string>({
      query: (assignmentId) => ({
        url: `/training/assignments/${assignmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TrainingAssignments'],
    }),

    // =====================================================
    // Content Sets
    // =====================================================

    createContentSet: builder.mutation<ContentSet, { ownerType: ContentSetOwnerType; ownerId: string; name: string; description?: string }>({
      query: (body) => ({
        url: '/content-sets',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<ContentSet>) => response.data,
      invalidatesTags: ['ContentSets'],
    }),

    listContentSets: builder.query<ContentSet[], { ownerType: ContentSetOwnerType; ownerId: string }>({
      query: ({ ownerType, ownerId }) => ({
        url: '/content-sets',
        params: { ownerType, ownerId },
      }),
      transformResponse: (response: ApiResponse<ContentSet[]>) => response.data,
      providesTags: ['ContentSets'],
    }),

    getContentSet: builder.query<ContentSetWithItems, string>({
      query: (contentSetId) => `/content-sets/${contentSetId}`,
      transformResponse: (response: ApiResponse<ContentSetWithItems>) => response.data,
      providesTags: (_result, _error, contentSetId) => [{ type: 'ContentSets', id: contentSetId }],
    }),

    deleteContentSet: builder.mutation<void, string>({
      query: (contentSetId) => ({
        url: `/content-sets/${contentSetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ContentSets'],
    }),

    addContentItem: builder.mutation<ContentItem, { contentSetId: string; title: string; type: ContentItemType; storagePathOrUrl: string; metadata?: Record<string, unknown> }>({
      query: ({ contentSetId, ...body }) => ({
        url: `/content-sets/${contentSetId}/items`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<ContentItem>) => response.data,
      invalidatesTags: (_result, _error, { contentSetId }) => [{ type: 'ContentSets', id: contentSetId }],
    }),

    removeContentItem: builder.mutation<void, { contentSetId: string; itemId: string }>({
      query: ({ contentSetId, itemId }) => ({
        url: `/content-sets/${contentSetId}/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { contentSetId }) => [{ type: 'ContentSets', id: contentSetId }],
    }),

    getContentSetIndexStatus: builder.query<ContentSetIndexStatus, string>({
      query: (contentSetId) => `/content-sets/${contentSetId}/index-status`,
      transformResponse: (response: ApiResponse<ContentSetIndexStatus>) => response.data,
      providesTags: (_result, _error, contentSetId) => [{ type: 'ContentSets', id: `${contentSetId}-index` }],
    }),

    reindexContentSet: builder.mutation<{ message: string }, string>({
      query: (contentSetId) => ({
        url: `/content-sets/${contentSetId}/reindex`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ message: string }>) => response.data,
      invalidatesTags: (_result, _error, contentSetId) => [
        { type: 'ContentSets', id: contentSetId },
        { type: 'ContentSets', id: `${contentSetId}-index` },
      ],
    }),

    // File upload mutation (uses FormData)
    uploadContentFile: builder.mutation<
      { itemId: string; filename: string; textExtracted: boolean; chunksIndexed: number },
      { contentSetId: string; file: File }
    >({
      query: ({ contentSetId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/content-sets/${contentSetId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: ApiResponse<{ itemId: string; filename: string; textExtracted: boolean; chunksIndexed: number }>) => response.data,
      invalidatesTags: (_result, _error, { contentSetId }) => [
        { type: 'ContentSets', id: contentSetId },
        { type: 'ContentSets', id: `${contentSetId}-index` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Organizations
  useListOrganizationsQuery,
  useGetOrganizationBySlugQuery,
  // Training Modules
  useListTrainingModulesQuery,
  useGetTrainingModuleQuery,
  useGetTrainingAuthoringOptionsQuery,
  useCreateTrainingModuleMutation,
  useSaveTrainingModuleDraftMutation,
  usePublishTrainingModuleMutation,
  useSetTrainingModuleArchivedMutation,
  // Training Assignments
  useCreateAssignmentsMutation,
  useListMyAssignmentsQuery,
  useListOrgAssignmentsQuery,
  useStartAssignmentSessionMutation,
  useDeleteAssignmentMutation,
  // Content Sets
  useCreateContentSetMutation,
  useListContentSetsQuery,
  useGetContentSetQuery,
  useDeleteContentSetMutation,
  useAddContentItemMutation,
  useRemoveContentItemMutation,
  useGetContentSetIndexStatusQuery,
  useReindexContentSetMutation,
  useUploadContentFileMutation,
} = trainingApi;
